/**
 * SDK Event Types
 * Custom events dispatched by SDK components
 */

import type { Product } from './product';
import type { LocalCartItem } from './cart';

/**
 * Base event detail interface
 */
export interface KhaimeEventDetail {
  timestamp: number;
  merchantId: string | number;
}

/**
 * Product click event
 */
export interface ProductClickEventDetail extends KhaimeEventDetail {
  product: Product;
  source: 'grid' | 'card' | 'detail';
}

/**
 * Product add to cart event
 */
export interface AddToCartEventDetail extends KhaimeEventDetail {
  product: Product;
  quantity: number;
  variant?: { id: number; name: string };
}

/**
 * Buy now event (direct checkout for non-physical products)
 */
export interface BuyNowEventDetail extends KhaimeEventDetail {
  product: Product;
  quantity: number;
  variant?: { id: number; name: string };
}

/**
 * Checkout initiated event
 */
export interface CheckoutInitEventDetail extends KhaimeEventDetail {
  productId: number;
  amount: number;
  currency: string;
  sessionToken?: string;
}

/**
 * Payment success event
 */
export interface PaymentSuccessEventDetail extends KhaimeEventDetail {
  sessionToken: string;
  orderId?: string;
  amount: number;
  currency: string;
  paymentMethod: 'stripe' | 'paystack';
}

/**
 * Payment error event
 */
export interface PaymentErrorEventDetail extends KhaimeEventDetail {
  error: string;
  code?: string;
  sessionToken?: string;
}

/**
 * Currency change event
 */
export interface CurrencyChangeEventDetail extends KhaimeEventDetail {
  previousCurrency: string;
  newCurrency: string;
}

/**
 * Config loaded event
 */
export interface ConfigLoadedEventDetail extends KhaimeEventDetail {
  config: Record<string, unknown>;
  source: 'cache' | 'api';
}

/**
 * Cart updated event
 */
export interface CartUpdatedEventDetail extends KhaimeEventDetail {
  items: LocalCartItem[];
  itemCount: number;
  subtotal: number;
  action: 'add' | 'remove' | 'update' | 'clear' | 'hydrate';
}

/**
 * Cart open/close events
 */
export interface CartOpenEventDetail extends KhaimeEventDetail {}
export interface CartCloseEventDetail extends KhaimeEventDetail {}

/**
 * Product detail open/close events
 */
export interface ProductDetailOpenEventDetail extends KhaimeEventDetail {
  product: Product;
}

export interface ProductDetailCloseEventDetail extends KhaimeEventDetail {
  productId: number;
}

/**
 * Plan selected event (pricing component)
 */
export interface PlanSelectedEventDetail extends KhaimeEventDetail {
  product: Product;
  planKey?: string;
  frequency: string;
  price: number;
  currency: string;
}

/**
 * Event type map for type-safe event handling
 */
export interface KhaimeEventMap {
  'khaime:product-click': CustomEvent<ProductClickEventDetail>;
  'khaime:add-to-cart': CustomEvent<AddToCartEventDetail>;
  'khaime:buy-now': CustomEvent<BuyNowEventDetail>;
  'khaime:checkout-init': CustomEvent<CheckoutInitEventDetail>;
  'khaime:payment-success': CustomEvent<PaymentSuccessEventDetail>;
  'khaime:payment-error': CustomEvent<PaymentErrorEventDetail>;
  'khaime:currency-change': CustomEvent<CurrencyChangeEventDetail>;
  'khaime:config-loaded': CustomEvent<ConfigLoadedEventDetail>;
  'khaime:cart-updated': CustomEvent<CartUpdatedEventDetail>;
  'khaime:cart-open': CustomEvent<CartOpenEventDetail>;
  'khaime:cart-close': CustomEvent<CartCloseEventDetail>;
  'khaime:product-detail-open': CustomEvent<ProductDetailOpenEventDetail>;
  'khaime:product-detail-close': CustomEvent<ProductDetailCloseEventDetail>;
  'khaime:plan-selected': CustomEvent<PlanSelectedEventDetail>;
}

/**
 * Create a typed custom event
 */
export function createKhaimeEvent<K extends keyof KhaimeEventMap>(
  type: K,
  detail: Omit<KhaimeEventMap[K]['detail'], 'timestamp'>
): KhaimeEventMap[K] {
  return new CustomEvent(type, {
    detail: {
      ...detail,
      timestamp: Date.now(),
    },
    bubbles: true,
    composed: true, // Crosses Shadow DOM boundary
  }) as KhaimeEventMap[K];
}

/**
 * Callback types for SDK options
 */
export interface SDKCallbacks {
  onProductClick?: (product: Product) => void;
  onAddToCart?: (product: Product, quantity: number) => void;
  onCheckoutInit?: (sessionToken: string) => void;
  onPaymentSuccess?: (result: PaymentSuccessEventDetail) => void;
  onPaymentError?: (error: PaymentErrorEventDetail) => void;
  onCurrencyChange?: (currency: string) => void;
  onCartUpdated?: (items: LocalCartItem[], action: string) => void;
  onCartOpen?: () => void;
  onCartClose?: () => void;
  onProductDetailOpen?: (product: Product) => void;
  onProductDetailClose?: () => void;
  onPlanSelected?: (detail: PlanSelectedEventDetail) => void;
  onReady?: () => void;
  onError?: (error: Error) => void;
}
